import { useEffect, useMemo, useRef } from 'react';

import { useGetVariantContentOptions } from 'features/menu/hooks/use-get-variant-content-options';

import { MenuObjectTypes } from 'enums/menu';
import {
  IGetComboPlusQuery,
  IGetItemPlusQuery,
  useGetComboPlusQuery,
  useGetItemPlusQuery,
} from 'generated/sanity-graphql';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { withSimpleCache } from 'state/graphql/with-simple-cache';
import { IMenuObject } from 'state/menu/types';

import { buildVendorVariables } from './build-vendor-variables';
import { getValidDataFromQuery } from './get-valid-data-from-query';
import { IGetPickerPlusByVendorQuery } from './picker-batched-query-types';
import { usePickerBatchedQuery } from './use-picker-batched-query';

const useComboQuery = withSimpleCache(useGetComboPlusQuery);
const useItemQuery = withSimpleCache(useGetItemPlusQuery);

export const useGetMenuObjectQuery = ({
  sanityId,
  menuObjectType,
  onCompleted,
}: {
  sanityId: string;
  menuObjectType: MenuObjectTypes;
  onCompleted?: (data: IMenuObject) => void;
}) => {
  const type = menuObjectType;
  const { vendor: posVendor } = usePosVendor();

  const getVariantContentOptions = useGetVariantContentOptions();

  const getQueryOptions = (menuObjectType: MenuObjectTypes) => ({
    variables: { id: sanityId },
    skip: !sanityId || type !== menuObjectType,
    onCompleted: (data: IGetComboPlusQuery | IGetItemPlusQuery | IGetPickerPlusByVendorQuery) => {
      const menuObject = getValidDataFromQuery(data, getVariantContentOptions);
      if (menuObject && onCompleted) {
        onCompleted(menuObject);
      }
    },
  });

  // Stable reference: only recomputes when posVendor actually changes.
  // Without useMemo, buildVendorVariables returns a new object every render,
  // which causes usePickerBatchedQuery to loop infinitely.
  const vendorVariables = useMemo(() => buildVendorVariables(posVendor), [posVendor]);

  const {
    data: comboData,
    error: comboError,
    loading: comboLoading,
    refetch: refetchCombo,
  } = useComboQuery(getQueryOptions(MenuObjectTypes.COMBO));

  const {
    data: itemData,
    error: itemError,
    loading: itemLoading,
    refetch: refetchItem,
  } = useItemQuery(getQueryOptions(MenuObjectTypes.ITEM));

  // Batched picker: shell (pass 1) + batched items/combos (passes 2 and 3).
  // Replaces the monolithic GetPickerPlusByVendor query that exceeds Sanity's
  // 5MB limit for large pickers like picker_5520.
  const {
    data: pickerData,
    error: pickerError,
    loading: pickerLoading,
    refetch: refetchPicker,
  } = usePickerBatchedQuery({
    id: sanityId,
    skip: !sanityId || type !== MenuObjectTypes.PICKER || !posVendor,
    vendorVariables,
  });

  // Fire onCompleted for picker exactly once when the two-pass result arrives.
  // We key the ref on the pickerData object reference (stable after assembly),
  // so we never re-fire on subsequent re-renders that produce a new IMenuObject.
  const calledOnCompletedForRef = useRef<unknown>(null);
  useEffect(() => {
    if (
      pickerData &&
      onCompleted &&
      type === MenuObjectTypes.PICKER &&
      !pickerLoading &&
      calledOnCompletedForRef.current !== pickerData
    ) {
      calledOnCompletedForRef.current = pickerData;
      const menuObject = getValidDataFromQuery(
        pickerData as IGetPickerPlusByVendorQuery,
        getVariantContentOptions
      );
      if (menuObject) {
        onCompleted(menuObject);
      }
    }
    // pickerData is the stable gate; changing it is the only time we should re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerData, pickerLoading]);

  const data = getValidDataFromQuery(
    comboData || itemData || (pickerData as IGetPickerPlusByVendorQuery | undefined),
    getVariantContentOptions
  );
  const error =
    type === MenuObjectTypes.COMBO
      ? comboError
      : type === MenuObjectTypes.ITEM
      ? itemError
      : pickerError;
  const refetch =
    type === MenuObjectTypes.COMBO
      ? refetchCombo
      : type === MenuObjectTypes.ITEM
      ? refetchItem
      : refetchPicker;

  return {
    data,
    error,
    loading: comboLoading || itemLoading || pickerLoading,
    refetch,
  };
};
